<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { BucketUpdateDocument } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BModal,
  BSpinner,
} from 'bootstrap-vue-next';

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  bucketId: number;
  bucketName: string;
  bucketFriendlyName: string;
  bucketDescription: string;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const bucketFriendlyName = ref<string>(props.bucketFriendlyName);
const bucketDescription = ref<string>(props.bucketDescription);

const show = computed<boolean>(() => {
  return props.show;
});

// --- Mutation: Rename Data Object ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(BucketUpdateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.bucketUpdate) {
    console.debug('Bucket updated:', res.data.bucketUpdate);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Data update failed: ' + res.data;
    console.error('Bucket update failed:', res.data);
  }
});
onMutationError((err) => {
  console.error('Error updating bucket:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function onBucketUpdateConfirmed() {
  if (!bucketFriendlyName.value) {
    mutationError.value = 'You must provide data label';
    console.error('Data label not provided');
    return;
  }
  console.debug('Updating bucket:', props.bucketName, '(', props.bucketId, ')');

  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation({
    id: props.bucketId,
    friendlyName: bucketFriendlyName.value,
    description: bucketDescription.value,
  });
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <div>
    <b-modal v-model="show" @hide="handleCloseModal">
      <!-- Modal Title -->
      <template #title>
        <font-awesome-icon icon="pencil" class="me-2" />
        Update Data Details
      </template>
      <!-- Modal Content -->
      <div>
        <!-- Project name -->
        <BFormGroup label="Data label" label-for="new-bucket-name" class="mt-3">
          <BFormInput
            v-model="bucketFriendlyName"
            id="new-bucket-name"
            name="new-bucket-name"
            placeholder="Enter a name for the bucket"
            required
          ></BFormInput>
        </BFormGroup>
        <!-- Sample description -->
        <BFormGroup
          label="Data description"
          label-for="new-sample-description"
          class="mt-3"
        >
          <BFormTextarea
            v-model="bucketDescription"
            id="new-bucket-description"
            name="new-bucket-description"
            placeholder="A brief description of the data."
          ></BFormTextarea>
        </BFormGroup>

        <!-- Error Message -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>

      <!-- Modal Footer -->
      <template #footer>
        <b-button variant="secondary" @click="handleCloseModal">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="onBucketUpdateConfirmed"
          :disabled="mutationInProgress || mutationShowSuccess"
        >
          <span v-if="mutationInProgress">
            Updating Data
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            Data Updated
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Update Data </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
